import { SearchResult } from '../models/search-result.model';
import { RESULT_LINK_CONFIGS } from '../constants/result-link-configs';
import { ResultLinkConfig, ResultLinkMatchMode } from '../models/result-link-config.model';

export interface ResolvedResultLink {
  href: string;
  target: '_blank' | '_self';
  rel: string | null;
}

export function resolveResultLink(item: SearchResult): ResolvedResultLink | null {
  for (const config of RESULT_LINK_CONFIGS) {
    if (!matchesLinkConfig(item, config)) {
      continue;
    }

    const href = resolveUrl(item, config.urlTemplate);
    if (!href) {
      continue;
    }

    const target: '_blank' | '_self' = config.openInNewTab === false ? '_self' : '_blank';
    return {
      href,
      target,
      rel: target === '_blank' ? 'noopener noreferrer' : null
    };
  }

  return null;
}

function matchesLinkConfig(item: SearchResult, config: ResultLinkConfig): boolean {
  const values = getFacetValuesForLinkConfig(item, config);
  if (!values.length) {
    return false;
  }

  const normalizedExpected = config.values
    .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .filter(Boolean);

  if (!normalizedExpected.length) {
    return false;
  }

  return values.some(value => normalizedExpected.includes(value));
}

function getFacetValuesForLinkConfig(item: SearchResult, config: ResultLinkConfig): string[] {
  const mode: ResultLinkMatchMode = config.matchMode ?? 'id';
  switch (config.facetKey) {
    case 'type':
    case 'typeId':
      return extractSingleValue(item.typeId, item.typeLabel, mode);
    case 'availabilityType':
    case 'availabilityTypeId':
      return extractSingleValue(item.availabilityTypeId, item.availabilityTypeLabel, mode);
    case 'domain':
    case 'domainId':
      return extractSingleValue(item.domainId, item.domainLabel, mode);
    case 'category':
    case 'categoryId':
      return extractSingleValue(item.categoryId, item.categoryLabel, mode);
    case 'targetAudience':
      return extractMultiValue(item.targetAudience ?? [], mode);
    case 'areas':
      return extractMultiValue(item.areas ?? [], mode);
    case 'annexes':
      return extractMultiValue(item.annexes ?? [], mode);
    default:
      return [];
  }
}

function extractSingleValue(
  idValue: string | null | undefined,
  labelValue: string | null | undefined,
  mode: ResultLinkMatchMode = 'id'
): string[] {
  const normalizedId = typeof idValue === 'string' ? idValue.trim() : '';
  const normalizedLabel = typeof labelValue === 'string' ? labelValue.trim() : '';

  switch (mode) {
    case 'label':
      return normalizedLabel ? [normalizedLabel.toLowerCase()] : [];
    case 'value':
      if (normalizedLabel) {
        return [normalizedLabel.toLowerCase()];
      }
      return normalizedId ? [normalizedId.toLowerCase()] : [];
    case 'id':
    default:
      return normalizedId ? [normalizedId.toLowerCase()] : [];
  }
}

function extractMultiValue(
  collection: (string | null | undefined)[],
  mode: ResultLinkMatchMode = 'value'
): string[] {
  return collection
    .map(value => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .filter(Boolean);
}

function resolveUrl(item: SearchResult, template: string): string | null {
  if (!template) {
    return null;
  }

  let hasMissingToken = false;
  const result = template.replace(/\{([^}]+)\}/g, (_, token: string) => {
    const rawValue = getTemplateTokenValue(item, token);
    if (rawValue == null) {
      hasMissingToken = true;
      return '';
    }
    return encodeURIComponent(rawValue);
  });

  return hasMissingToken ? null : result;
}

function getTemplateTokenValue(item: SearchResult, token: string): string | null {
  const source = item as unknown as Record<string, unknown>;
  const rawValue = source[token];

  if (rawValue == null) {
    return null;
  }

  if (Array.isArray(rawValue)) {
    return rawValue.length ? String(rawValue[0]) : null;
  }

  if (typeof rawValue === 'string') {
    return rawValue;
  }

  if (typeof rawValue === 'number' || typeof rawValue === 'bigint') {
    return String(rawValue);
  }

  return null;
}
